import type { StorageLike } from './types';

export const WORKSPACE_UI_STORAGE_KEY = 'cpq-v2-workspace-ui:v1';
export const PICKER_DEFAULT_WIDTH = 270;
export const PICKER_MIN_WIDTH = 240;
export const PICKER_MAX_WIDTH = 420;
export const PICKER_COLLAPSED_WIDTH = 44;
export const PICKER_COLLAPSE_THRESHOLD = 180;
const APP_NAV_WIDTH = 58;
const QUOTE_CANVAS_MIN_WIDTH = 720;

export interface WorkspacePreferences {
  pickerWidth: number;
  pickerCollapsed: boolean;
}

export interface WorkspacePreferencesPort {
  load: (viewportWidth: number) => WorkspacePreferences;
  save: (preferences: WorkspacePreferences) => void;
}

export function createWorkspacePreferencesPort(
  storage: StorageLike,
): WorkspacePreferencesPort {
  return {
    load: (viewportWidth) => loadWorkspacePreferences(storage, viewportWidth),
    save: (preferences) => persistWorkspacePreferences(storage, preferences),
  };
}

export const DEFAULT_WORKSPACE_PREFERENCES: WorkspacePreferences = {
  pickerWidth: PICKER_DEFAULT_WIDTH,
  pickerCollapsed: false,
};

export function maxPickerWidth(viewportWidth: number): number {
  return Math.max(
    PICKER_MIN_WIDTH,
    Math.min(
      PICKER_MAX_WIDTH,
      viewportWidth - APP_NAV_WIDTH - QUOTE_CANVAS_MIN_WIDTH,
    ),
  );
}

export function clampPickerWidth(
  width: number,
  viewportWidth = Number.POSITIVE_INFINITY,
): number {
  const upper = Number.isFinite(viewportWidth)
    ? maxPickerWidth(viewportWidth)
    : PICKER_MAX_WIDTH;
  return Math.min(upper, Math.max(PICKER_MIN_WIDTH, Math.round(width)));
}

export function loadWorkspacePreferences(
  storage: StorageLike,
  viewportWidth = Number.POSITIVE_INFINITY,
): WorkspacePreferences {
  const raw = storage.getItem(WORKSPACE_UI_STORAGE_KEY);
  if (!raw) {
    return {
      ...DEFAULT_WORKSPACE_PREFERENCES,
      pickerWidth: clampPickerWidth(PICKER_DEFAULT_WIDTH, viewportWidth),
    };
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return { ...DEFAULT_WORKSPACE_PREFERENCES };
    }
    const candidate = parsed as Partial<WorkspacePreferences>;
    return {
      pickerWidth: clampPickerWidth(
        typeof candidate.pickerWidth === 'number'
          ? candidate.pickerWidth
          : PICKER_DEFAULT_WIDTH,
        viewportWidth,
      ),
      pickerCollapsed: candidate.pickerCollapsed === true,
    };
  } catch {
    return { ...DEFAULT_WORKSPACE_PREFERENCES };
  }
}

export function persistWorkspacePreferences(
  storage: StorageLike,
  preferences: WorkspacePreferences,
): void {
  storage.setItem(WORKSPACE_UI_STORAGE_KEY, JSON.stringify(preferences));
}
